import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { siteAddress, tenantDisplayName } from "./tenantDisplay";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { formatMoneyLedger } from "../lib/formatMoney";
import { PageLoadError } from "./NoAccess";
import { useSecondaryRead } from "./screenAccess";
import { pluralKey } from "../lib/pluralKey";
import CurrencyPicker from "./charts/CurrencyPicker";
import DailyRevenueChart from "./charts/DailyRevenueChart";
import { currenciesOf } from "./charts/currencies";
import { currencyLabel } from "../lib/currencies";
import DayXAxis from "./charts/DayXAxis";
import { formatDayLong, formatCount } from "./charts/chartFormat";
import { CHART_GRID, CHART_TICK, CHART_TOOLTIP, CHART_LEGEND, CHART_SERIES } from "./charts/chartTheme";
import { Loader2, DollarSign, TrendingUp, Award, Activity as ActivityIcon, CalendarRange } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const RANGES = [30, 90, 180, 365];
const DEFAULT_RANGE = 90;

/** `/finance` serves major units; the money formatters take integer cents. */
const cents = (major) => Math.round((Number(major) || 0) * 100);

/**
 * Signups and churn share one dense UTC-day axis (the server builds it), so the two
 * series are zipped by index. A mismatch means the server broke that guarantee: say so in
 * development instead of plotting two series against the wrong days.
 */
function zipSignupsChurn(signups, churn) {
  return (signups || []).map((s, i) => {
    const c = (churn || [])[i];
    if (process.env.NODE_ENV !== "production" && c && c.date !== s.date) {
      console.warn(`[finance] signups/churn axes differ at index ${i}: ${s.date} vs ${c.date}`);
    }
    return { date: s.date, signups: s.count, churn: c ? c.count : 0 };
  });
}

export default function FinanceDashboard() {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  const [days, setDays] = useState(DEFAULT_RANGE);
  // The period read has its own state: it must fail, load and retry without taking the
  // since-launch cards with it. `ts` is null while loading or after a failure.
  const [ts, setTs] = useState(null);
  const [tsError, setTsError] = useState(null);
  const [tsAttempt, setTsAttempt] = useState(0);
  const [picked, setPicked] = useState(null);
  // The period read is finance.read, which not every super admin holds (screenAccess.js,
  // SECONDARY_READS): without it the read is never sent and the period section is left out.
  const canReadSeries = useSecondaryRead("financeSeries");

  useEffect(() => {
    setLoadError(null);
    api.get("/super-admin/finance").then((r) => setData(r.data)).catch(setLoadError);
  }, [attempt]);

  useEffect(() => {
    // `live` is per effect run: an answer for an earlier range that lands after a newer
    // click is dropped, so 30-day figures never sit under a "365 days" heading.
    if (!canReadSeries) return undefined;
    let live = true;
    setTs(null);
    setTsError(null);
    api.get("/super-admin/finance/timeseries", { params: { days } })
      .then((r) => { if (live) setTs(r.data); })
      .catch((e) => { if (live) setTsError(e); });
    return () => { live = false; };
  }, [days, tsAttempt, canReadSeries]);

  if (loadError) return <PageLoadError error={loadError} onRetry={() => setAttempt((n) => n + 1)} testId="sa-finance-load-error" />;
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-amber-600" />;

  const money = (major, currency) => formatMoneyLedger(cents(major), currency, lang);
  const centsMoney = (c, currency) => formatMoneyLedger(c, currency, lang);
  // Each currency is ranked on its own: the server never sends a list that mixes them.
  const topByCurrency = data.top_tenants_by_currency || {};
  const topCurrencies = currenciesOf([topByCurrency], data.settlement_currency)
    .filter((c) => (topByCurrency[c] || []).length > 0);

  const renderPeriod = () => {
    if (tsError) {
      return <PageLoadError error={tsError} onRetry={() => setTsAttempt((n) => n + 1)} testId="fin-period-load-error" />;
    }
    if (!ts) {
      return <Loader2 className="w-6 h-6 animate-spin text-amber-600" data-testid="fin-period-loading" />;
    }
    const totals = ts.revenue_total_cents_by_currency || {};
    const currencies = currenciesOf([totals], ts.settlement_currency);
    // A currency that left the window (a shorter range) falls back to the settlement one.
    const currency = currencies.includes(picked) ? picked : ts.settlement_currency;
    const revenueSeries = (ts.revenue_cents_by_currency || {})[currency] || [];
    const activity = zipSignupsChurn(ts.signups, ts.churn);
    const hasActivity = activity.some((r) => r.signups > 0 || r.churn > 0);

    return (
      <>
        <div className="mb-6 text-sm text-slate-500" data-testid="fin-period-totals">
          <div className="font-medium text-slate-700 mb-2">{t("fin.periodRevenue")}</div>
          <div className="flex flex-wrap gap-x-8 gap-y-1 mb-2">
            {currencies.map((c) => (
              <span key={c} className="font-sol font-semibold text-xl text-slate-800" data-testid={`fin-period-total-${c}`}>
                {centsMoney(totals[c] || 0, c)}
              </span>
            ))}
          </div>
          <div>{t(pluralKey(lang, ts.transactions_total, "fin.periodTx"), { n: formatCount(ts.transactions_total, lang) })}</div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="rounded-xl border border-slate-200 p-6" data-testid="chart-revenue">
            <div className="flex items-center justify-between gap-2 mb-4">
              <h3 className="font-sol font-semibold text-lg flex items-center gap-2">
                <ActivityIcon className="w-4 h-4 text-amber-600" /> {t("fin.revenuePerDay")}
              </h3>
              <CurrencyPicker currencies={currencies} value={currency} onChange={setPicked}
                testid="fin-revenue" label={t("fin.revenueCurrency")} />
            </div>
            {!totals[currency] ? (
              <p className="text-slate-400 text-sm py-12 text-center">{t("fin.noTxPeriod")}</p>
            ) : (
              <DailyRevenueChart series={revenueSeries} currency={currency} lang={lang}
                label={t("fin.revenuePerDay")} testid="chart-revenue-plot" />
            )}
          </div>

          <div className="rounded-xl border border-slate-200 p-6" data-testid="chart-signups-churn">
            <h3 className="font-sol font-semibold text-lg mb-4">{t("fin.signupsChurn")}</h3>
            {!hasActivity ? (
              <p className="text-slate-400 text-sm py-12 text-center">{t("fin.noActivityPeriod")}</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={activity}>
                  <CartesianGrid {...CHART_GRID} />
                  <DayXAxis dates={activity.map((p) => p.date)} lang={lang} />
                  <YAxis tick={CHART_TICK} allowDecimals={false} tickFormatter={(v) => formatCount(v, lang)} />
                  <Tooltip {...CHART_TOOLTIP} labelFormatter={(d) => formatDayLong(d, lang)} />
                  <Legend {...CHART_LEGEND} />
                  <Bar dataKey="signups" name={t("fin.legendSignups")} fill={CHART_SERIES.positive} />
                  <Bar dataKey="churn" name={t("fin.legendChurn")} fill={CHART_SERIES.negative} />
                </BarChart>
              </ResponsiveContainer>
            )}
            <p className="text-xs text-slate-500 mt-3" data-testid="churn-definition">{t("fin.churnDefinition")}</p>
          </div>
        </div>
      </>
    );
  };

  return (
    <div data-testid="sa-finance">
      <div className="mb-8" data-testid="fin-header">
        <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800">{t("fin.title")}</h1>
        <p className="text-slate-500">{t("fin.subtitle")}</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="rounded-2xl bg-white border border-slate-200 p-8" data-testid="fin-mrr">
          <h3 className="font-sol font-semibold text-xl mb-1 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-amber-600" /> {t("fin.mrrByCurrency")}
          </h3>
          <p className="text-xs text-slate-500 mb-5">{t("fin.mrrHint")}</p>
          <div className="space-y-3">
            {Object.entries(data.mrr_by_currency || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500 text-sm uppercase">{k}</span>
                <div className="text-right">
                  <div className="font-sol font-semibold text-xl">{money(v, k)}</div>
                  <div className="text-xs text-slate-400">{t("fin.arr")} {money(data.arr_projection_by_currency?.[k] || 0, k)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 p-8" data-testid="fin-total-revenue">
          <h3 className="font-sol font-semibold text-xl mb-1 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-amber-600" /> {t("fin.totalRevenue")}
          </h3>
          <p className="text-xs text-slate-500 mb-5">{t("fin.totalRevenueHint")}</p>
          <div className="space-y-3">
            {Object.entries(data.total_revenue_by_currency || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500 text-sm uppercase">{k}</span>
                <span className="font-sol font-semibold text-xl">{money(v, k)}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-6 border-t border-slate-100 text-sm text-slate-500">
            {t(pluralKey(lang, data.completed_transactions, "fin.completedTx"), { n: formatCount(data.completed_transactions, lang) })}
          </div>
        </div>
      </div>

      {canReadSeries && (
        <section className="rounded-2xl bg-white border border-slate-200 p-8 mb-6" data-testid="fin-period">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h2 className="font-sol font-semibold text-xl flex items-center gap-2">
              <CalendarRange className="w-5 h-5 text-amber-600" /> {t("fin.periodTitle", { n: days })}
            </h2>
            <div className="flex gap-2" role="group" aria-label={t("fin.rangeLabel")}>
              {RANGES.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDays(d)}
                  aria-pressed={days === d}
                  className={`px-3 py-1.5 rounded-lg text-xs ${days === d ? "bg-amber-100 text-amber-600 border border-amber-200" : "bg-white shadow-sm text-slate-500 border border-slate-200"}`}
                  data-testid={`range-${d}`}
                >
                  {d}{t("fin.daysSuffix")}
                </button>
              ))}
            </div>
          </div>
          {renderPeriod()}
        </section>
      )}

      <div className="rounded-2xl bg-white border border-slate-200 p-8" data-testid="fin-top">
        <h3 className="font-sol font-semibold text-xl mb-1 flex items-center gap-2">
          <Award className="w-5 h-5 text-amber-600" /> {t("fin.topTenants")}
        </h3>
        <p className="text-xs text-slate-500 mb-5">{t("fin.topTenantsHint")}</p>
        {topCurrencies.length === 0 && <p className="text-slate-500 text-sm py-6 text-center">{t("fin.noTopTenants")}</p>}
        <div className="space-y-6">
          {topCurrencies.map((cur) => (
            <div key={cur} data-testid={`fin-top-group-${cur}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">{currencyLabel(cur)}</div>
              <div className="space-y-2">
                {topByCurrency[cur].map((tn, i) => {
                  const address = siteAddress(tn.website);
                  return (
                    <a key={tn.tenant_id} href={`/super-admin/tenants/${tn.tenant_id}`} data-testid={`fin-top-${tn.tenant_id}`}
                      className="flex items-center gap-4 py-2 border-b border-slate-100 hover:bg-slate-50 -mx-2 px-2 rounded-lg">
                      <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-600 text-xs font-medium flex items-center justify-center">{i + 1}</div>
                      <div className="flex-1">
                        <div className="text-sm">{tenantDisplayName(tn)}</div>
                        <div className="text-xs text-slate-400">
                          {tn.plan_name || tn.plan_id}
                          {address && (
                            <>
                              {" · "}
                              <span title={tn.website} data-testid={`fin-top-${tn.tenant_id}-site`}>{address}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="font-sol font-semibold">{money(tn.amount, tn.currency)}</div>
                    </a>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
